import React, { useEffect, useRef } from 'react';
import { Chip, Select, MenuItem, Button } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Grid } from '@material-ui/core';
import JsonEditor from '../../components/JsonEditor';
import moment from 'moment';
import { useStyles } from './style';
import { LuiMaterialTable } from '@jda/lui-common-component-library';
import ViewDetails from '@jda/lui-common-icon-library/ui/ViewDetails';
import { formatString } from '../../utils/helpers';

interface ErrorDetailsProps {
  data: any;
  children: any;
}

export const VerticalLinearStepper = (props: ErrorDetailsProps) => {
  const { data, children } = props;
  const classes = useStyles();
  const [detailsType, setDetailsType] = React.useState('Payloads');
  const [messageType, setMessageType] = React.useState('Request Response');
  const [steps, setSteps] = React.useState<any>([]);
  const [request, setRequest] = React.useState<any>([]);
  const [response, setResponse] = React.useState<any>([]);
  const [selectedStep, setSelectedStep] = React.useState<any>([]);
  const [activeInex, setActiveIndex] = React.useState<any>(0);
  const [expand, setExpand] = React.useState<boolean>(false);
  const [tableData, setTableData] = React.useState<any>([]);
  const [viewDetails, setViewDetails] = React.useState<any>('');

  const jsonRequestEditorRef = useRef<any>();
  const jsonResponseEditorRef = useRef<any>();

  const adminCoulmns = [
    {
      title: 'Activity Type',
      field: 'activityType'
    },
    {
      title: 'Start Time',
      field: 'startTime'
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData: any) => (
        <span style={{ color: `${rowData.status !== 'SUCCESS' ? '#EF4242' : ''}` }}>{rowData.status}</span>
      )
    }
  ];

  useEffect(() => {
    if (data) getSteps(data);
  }, [data]);

  useEffect(() => {
    if (jsonRequestEditorRef && jsonRequestEditorRef.current) {
      jsonRequestEditorRef.current.set(request);
      jsonRequestEditorRef.current.expandAll && jsonRequestEditorRef.current.expandAll();
    }
    if (jsonResponseEditorRef && jsonResponseEditorRef.current) {
      jsonResponseEditorRef.current.set(response);
      jsonResponseEditorRef.current.expandAll && jsonResponseEditorRef.current.expandAll();
    }
  }, [request, response]);

  const getSteps = (auditData: any) => {
    const res = auditData?.requestResponse?.auditInfo?.reverse()?.map((step: any) => {
      const infoObj: any = {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
      step?.auditInfos.map((audit: any) => {
        if (audit?.type === 'PAYLOADS') {
          infoObj['payloadInfo'] = audit.info;
        }
      });
      return {
        requestType: step.requestType,
        time: moment(step.executionDate).format('YYYY-MM-DD HH:m:s'),
        ...infoObj
      };
    });
    getTableData(auditData, res[0]);
    setSelectedStep(res[0]);
    setSteps(res);
    setMessageType('Request Response');
    if (res[0]?.payloadInfo?.requestReceived) setRequest(formatString(res[0]?.payloadInfo?.requestReceived));
    if (res[0]?.payloadInfo?.responseSent) setResponse(formatString(res[0]?.payloadInfo?.responseSent));
  };

  const getTableData = (auditData: any, selectedStep?: any) => {
    const tableData: any = [];
    const res = auditData?.requestResponse?.auditInfo?.reverse()?.map((step: any) => {
      step?.auditInfos.map((audit: any) => {
        if (
          audit?.type !== 'PAYLOADS' &&
          step?.executionDate === audit?.startTime &&
          selectedStep?.requestType === step?.requestType
        ) {
          tableData.push({
            activityType: audit?.type,
            startTime: moment(audit?.startTime).format('YYYY-MM-DD H:ss:mm'),
            status: audit?.status,
            info: audit?.info
          });
        }
      });
      setTableData(tableData);
    });
  };

  const getAvatar = (step: any) => {
    if (step?.requestType === 'NORMAL') return 'D';
    else if (step?.requestType === 'REPLAY') return 'R';
    else if (step?.requestType === 'SIMULATION') return 'S';
    else if (step?.requestType === 'RETRY') {
      return step?.retryCount || 'RT';
    }
  };
  const getColor = (step: any) => {
    if (step?.httpStatus >= 200 && step?.httpStatus <= 399) return '#00E676';
    else if (step?.requestType >= 400 && step?.httpStatus <= 499) return '#FAAF59';
    else return '#EF4242';
  };

  const handleTypeChange = (e: any) => {
    setDetailsType(e.target.value);
    if (e.target.value === 'Payloads' && selectedStep?.payloadInfo) {
      setMessageType('Request Response');
      setRequest(formatString(selectedStep?.payloadInfo?.requestReceived));
      setResponse(formatString(selectedStep?.payloadInfo?.responseSent));
    }
  };
  const handleMessageTypeChange = (e: any) => {
    setMessageType(e.target.value);
    const payloadInfo = selectedStep?.payloadInfo;
    if (
      e.target.value === 'Request Response' ||
      selectedStep?.requestType === 'REPLAY' ||
      selectedStep?.requestType === 'RETRY'
    ) {
      payloadInfo?.requestReceived ? setRequest(formatString(payloadInfo?.requestReceived)) : setRequest([]);
      payloadInfo?.responseSent ? setResponse(formatString(payloadInfo?.responseSent)) : setResponse([]);
    } else if (e.target.value === 'Response & Modified Response') {
      payloadInfo?.responseSent ? setRequest(formatString(payloadInfo?.responseSent)) : setRequest([]);
      payloadInfo?.responseReceived ? setResponse(formatString(payloadInfo?.responseReceived)) : setResponse([]);
    } else if (e.target.value === 'Request & Modified Request') {
      payloadInfo?.requestSent ? setRequest(formatString(payloadInfo?.requestSent)) : setRequest([]);
      payloadInfo?.requestReceived ? setResponse(formatString(payloadInfo?.requestReceived)) : setResponse([]);
    } else if (e.target.value === 'Additional Info') {
      setRequest(payloadInfo?.additionalInfo);
    }
  };

  const setStep = (step: any, index: any) => {
    setSelectedStep(step);
    setViewDetails('');
    if (data) getTableData(data, step);
    setActiveIndex(index);
  };

  const setRequestEditorRef = (instance: any) => {
    if (instance) {
      jsonRequestEditorRef.current = instance.jsonEditor;
    } else {
      jsonRequestEditorRef.current = null;
    }
  };

  const setResponseEditorRef = (instance: any) => {
    if (instance) {
      jsonResponseEditorRef.current = instance.jsonEditor;
    } else {
      jsonResponseEditorRef.current = null;
    }
  };

  const expandTimeline = () => {
    setExpand(!expand);
  };

  const archiveActions: any = [
    {
      icon: () => <ViewDetails />,
      onClick: (event: any, rowData: any) => handleViewDetails(rowData),
      tooltip: 'View Details',
      position: 'row'
    }
  ];

  const handleViewDetails = (rowData: any) => {
    setViewDetails(rowData);
  };

  return (
    <Grid container>
      <Grid item xs={expand ? 4 : 2}>
        <div className={classes.timeline}>
          {steps?.map((step: any, index: number) => (
            <div className={classes.timelinePost} onClick={() => setStep(step, index)}>
              <Grid container>
                <>
                  <Grid item xs={2} style={{ zIndex: 1 }}>
                    <Chip
                      size="medium"
                      label={getAvatar(step)}
                      style={{
                        backgroundColor: getColor(step)
                      }}
                      className={classes.timelineChip}
                      onClick={expandTimeline}
                    />
                  </Grid>
                  {expand && (
                    <Grid item xs={10} className={`${classes.requestContent} ${activeInex === index ? 'active' : ''}`}>
                      <p style={{ fontWeight: 500 }}>{step.requestType}</p>
                      <p style={{ fontWeight: 500 }}>{step.time}</p>
                    </Grid>
                  )}
                </>
              </Grid>
            </div>
          ))}
        </div>
      </Grid>
      <Grid item xs={expand ? 8 : 10} style={{ minWidth: '50%' }}>
        <Select
          style={{ width: '40%', textAlign: 'center', margin: '0 4px 10px 4px' }}
          data-id={`detailsType-`}
          id={`detailsType-`}
          value={detailsType}
          onChange={handleTypeChange}
        >
          <MenuItem value="Execution details">Execution details</MenuItem>
          <MenuItem value="Payloads">Payloads</MenuItem>
        </Select>
        {detailsType === 'Payloads' && (
          <Select
            style={{ width: '40%', textAlign: 'center', margin: '0 4px 10px 4px' }}
            data-id={`messageType-`}
            id={`messageType-`}
            value={messageType}
            onChange={handleMessageTypeChange}
          >
            <MenuItem value="Request Response">Request Response</MenuItem>
            <MenuItem value="Request & Modified Request">Request & Modified Request</MenuItem>
            <MenuItem value="Response & Modified Response">Response & Modified Response</MenuItem>
            <MenuItem value="Additional Info">Additional Info</MenuItem>
          </Select>
        )}

        {detailsType !== 'Payloads' && children}
        {detailsType === 'Execution details' && (
          <Grid container className={classes.details}>
            <Grid item xs={viewDetails === '' ? 12 : 6}>
              <LuiMaterialTable
                key="Execution Details"
                data={tableData}
                columns={adminCoulmns}
                materialTableProps={{
                  title: 'test',
                  actions: archiveActions,
                  options: { actionsColumnIndex: -1 }
                }}
              />
            </Grid>
            {viewDetails && (
              <Grid
                container
                item
                xs={6}
                style={{ padding: '20px', lineHeight: '2vh', borderLeft: '1px solid rgb(218 208 208);' }}
              >
                {Object.keys(viewDetails?.info).map((key: any) => (
                  <Grid item xs={6} spacing={4} alignItems="center" style={{ margin: '8px 0' }}>
                    {key} :{' '}
                    <b>
                      {key === 'startTime' || key === 'endTime'
                        ? moment(viewDetails?.info[key]).format('YYYY-MM-DD H:ss:mm')
                        : viewDetails?.info[key]}
                    </b>
                  </Grid>
                ))}
                <Button
                  data-id="ok-Button"
                  onClick={() => setViewDetails('')}
                  disabled={false}
                  color="primary"
                  autoFocus
                  variant="outlined"
                >
                  OK
                </Button>
              </Grid>
            )}
          </Grid>
        )}

        {detailsType === 'Payloads' && messageType !== '' && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>{messageType === 'Additional Info' ? 'Additional Info' : 'Request Info'}</Typography>
              <JsonEditor
                data={request}
                setRef={setRequestEditorRef}
                mode="code"
                view={false}
                id={'JsonRequestEditor'}
                allowedModes={['code', 'tree']}
                theme="ace/theme/github"
              />
            </Grid>
            {messageType !== 'Additional Info' && (
              <Grid item xs={6}>
                <Typography>Response Info</Typography>
                <JsonEditor
                  data={response}
                  setRef={setResponseEditorRef}
                  mode="code"
                  view={false}
                  id={'JsonResponseEditor'}
                  allowedModes={['code', 'tree']}
                  theme="ace/theme/github"
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
